import { forwardRef, useCallback, useLayoutEffect, useRef, useState } from 'react';
import { BasePopover } from './base';
import { useFieldControlId } from './fieldControlContext';
import ColorPickerPopover from './ColorPickerPopover';
import {
  createColorChangeEvent,
  DEFAULT_COLOR_HEX,
  getColorPickerStyle,
  normalizeColorHex,
} from './colorPickerUtils';

const SIZE_CLASS = {
  standard: '',
  small: 'ui-color-input--small',
};

function assignRef(target, value) {
  if (!target) {
    return;
  }

  if (typeof target === 'function') {
    target(value);
    return;
  }

  target.current = value;
}

const ColorInput = forwardRef(function ColorInput({
  id,
  name,
  value,
  defaultValue = DEFAULT_COLOR_HEX,
  onChange,
  onBlur,
  size = 'standard',
  muted = false,
  fluid = false,
  disabled = false,
  position = 'below',
  align = 'center',
  className = '',
  'aria-label': ariaLabel = 'Color',
  onMouseDown,
  onClick,
  style,
  focusOpenIntent = null,
  ...props
}, ref) {
  // The trigger is a <button>, which IS labelable, so it can adopt a FormField
  // caption's id. Deliberately separate from `id`: that one also rides the
  // synthetic change/blur payload (colorPickerUtils.js), and a generated useId
  // value has no business in an event contract. `id` stays the caller's, the DOM
  // gets whichever names it.
  const controlId = useFieldControlId(id);

  const triggerRef = useRef(null);
  const handledFocusOpenIntentRef = useRef(null);
  const [manuallyOpen, setManuallyOpen] = useState(false);
  const [dismissedFocusOpenIntent, setDismissedFocusOpenIntent] = useState(null);
  const intentOpen = !disabled
    && focusOpenIntent != null
    && dismissedFocusOpenIntent !== focusOpenIntent;
  const open = manuallyOpen || intentOpen;
  const resolvedColor = normalizeColorHex(value ?? defaultValue);
  const classes = [
    'ui-color-input',
    SIZE_CLASS[size],
    muted && 'ui-color-input--muted',
    fluid && 'ui-color-input--fluid',
    open && 'ui-color-input--active',
    className,
  ].filter(Boolean).join(' ');

  const setTriggerRef = useCallback((node) => {
    triggerRef.current = node;
    assignRef(ref, node);
  }, [ref]);

  const handleOpenChange = useCallback((nextOpen) => {
    if (open && !nextOpen) {
      onBlur?.(createColorChangeEvent(resolvedColor, { id, name }));
    }
    if (!nextOpen && focusOpenIntent != null) {
      setDismissedFocusOpenIntent(focusOpenIntent);
    }
    setManuallyOpen(nextOpen);
  }, [focusOpenIntent, id, name, onBlur, open, resolvedColor]);

  const handleMouseDown = useCallback((event) => {
    onMouseDown?.(event);
  }, [onMouseDown]);

  const handleClick = useCallback((event) => {
    onClick?.(event);
  }, [onClick]);

  useLayoutEffect(() => {
    if (
      disabled
      || focusOpenIntent == null
      || handledFocusOpenIntentRef.current === focusOpenIntent
    ) {
      return;
    }
    handledFocusOpenIntentRef.current = focusOpenIntent;
    triggerRef.current?.focus();
  }, [disabled, focusOpenIntent]);

  const trigger = (
    <button
      {...props}
      ref={setTriggerRef}
      id={controlId}
      type="button"
      disabled={disabled}
      aria-label={ariaLabel}
      className={classes}
      style={{
        ...getColorPickerStyle(resolvedColor),
        ...style,
      }}
      onMouseDown={handleMouseDown}
      onClick={handleClick}
    >
      <span className="ui-color-input__swatch" aria-hidden="true" />
    </button>
  );

  return (
    <BasePopover
      open={open}
      onOpenChange={handleOpenChange}
      trigger={trigger}
      side={position === 'above' ? 'top' : 'bottom'}
      align={align === 'left' ? 'start' : align === 'right' ? 'end' : 'center'}
      sideOffset={8}
      popupClassName="ui-color-picker-popover-shell"
      positionerClassName={`ui-color-picker-positioner ui-color-picker-positioner--${position}`}
      minWidth="var(--ui-color-picker-popover-width)"
      dismissContextRef={triggerRef}
      initialFocus={false}
    >
      <ColorPickerPopover
        id={id}
        name={name}
        value={resolvedColor}
        onChange={onChange}
        ariaLabel={ariaLabel}
      />
    </BasePopover>
  );
});

export default ColorInput;
